// A member's public profile at /p/<name>-<code>: their badge, streak and usual finish, their badges
// (tap one for its category), stats from games finished on their day, form, and recent achievements.
// Your own adds Share profile, Choose badge and the link; a rival's has Challenge (in the app) and
// your record against them; a guest arriving from a link gets the wordmark and a way into today's game.
// Guests have no profile. Unearned secrets never reach the page: the API sends them nameless.
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router'
import type { BadgeCategory, Challenge, Me, PlayerProfile } from '@wbwl/contract'
import { BadgeGrid, BadgePicker, CategorySheet, earnedOn, PhoenixCrown, SHORT_NAME } from '../components/achievements-ui.tsx'
import { Badge, NameBadge } from '../components/Badge.tsx'
import { recordText } from '../components/challenge-ui.tsx'
import { Icon } from '../components/Icon.tsx'
import { Avatar, ErrorNote, Loading, Logo, NotFound, useToast } from '../components/shell.tsx'
import { ApiFailure, api } from '../lib/api.ts'
import { useEdition, useMe, useMyAchievements, usePlayerProfile } from '../lib/data.ts'
import { fmt } from '../lib/game.ts'
import { copyText, nativeShare } from '../lib/share.ts'
import { invalidate, setResource } from '../lib/store.ts'
import { Form } from './You.tsx'

export default function ProfilePage() {
  const handle = useParams().handle ?? ''
  const q = usePlayerProfile(handle)
  const me = useMe().data
  if (q.error) {
    if (q.error instanceof ApiFailure && q.error.status === 404) return <NotFound />
    return <div className="page"><ErrorNote error={q.error} retry={() => q.reload()} /></div>
  }
  if (!q.data || !me) return <div className="page"><Loading label="Pulling their file…" /></div>
  return <Profile key={q.data.id} p={q.data} me={me} />
}

const sinceText = (date: string) => new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })

function Profile({ p, me }: { p: PlayerProfile; me: Me }) {
  const navigate = useNavigate()
  const toast = useToast()
  const entry = useLocation().key === 'default'
  const own = p.mine
  // A guest (most often someone who opened a shared link) gets the way in, not the head-to-head.
  const visitor = !own && me.kind === 'guest'
  const [open, setOpen] = useState<BadgeCategory | null>(null)
  const [picking, setPicking] = useState(false)
  const [wanted, setWanted] = useState(false)
  // Your own list, for the badge picker: only once you open it (the profile already has your badges).
  const mine = useMyAchievements(own && wanted).data
  const today = useEdition(visitor ? 'today' : null).data
  const url = `${window.location.origin}/p/${p.handle}`
  // On someone else's, their name comes into the top bar once the header has scrolled under it.
  const bar = useRef<HTMLDivElement>(null)
  const head = useRef<HTMLHeadingElement>(null)
  const [named, setNamed] = useState(false)
  useEffect(() => {
    if (own) return
    let frame = 0
    const check = () => {
      frame = 0
      const b = bar.current, h = head.current
      if (b && h) setNamed(h.getBoundingClientRect().bottom <= b.getBoundingClientRect().bottom)
    }
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(check) }
    check()
    addEventListener('scroll', onScroll, { passive: true })
    return () => { removeEventListener('scroll', onScroll); cancelAnimationFrame(frame) }
  }, [own])

  async function share() {
    const text = `${p.displayName} on Letters on Fire\n${url}`
    if ((await nativeShare(text)) === 'unavailable') copy()
  }
  async function copy() {
    toast(await copyText(url) ? <><Icon name="check" />Link copied</> : 'Couldn’t copy that')
  }

  const s = p.stats
  const stats: [string, string][] = [
    [s.bestFinish ? `#${fmt(s.bestFinish.position)}` : '—', s.bestFinish ? `best finish · No. ${s.bestFinish.edition}` : 'best finish'],
    [fmt(s.frontPages), `Front Page${s.frontPages === 1 ? '' : 's'}`],
    [s.best !== null ? fmt(s.best) : '—', 'best score'],
    [`${s.phoenix.raised}/${s.phoenix.tries}`, 'phoenixes raised'],
    [fmt(s.stopThePresses), 'Stop the Presses'],
    [s.allTime !== null ? `${s.allTime}%` : '—', 'of the solver, all time'],
  ]
  const tierOf = (c: BadgeCategory) => p.categories.find((x) => x.id === c)?.tier ?? 'locked'
  const phoenix = p.categories.find((c) => c.id === 'phoenix')
  const eight = p.categories.filter((c) => c.id !== 'phoenix')

  return (
    <div className={`page profile-page${visitor ? ' visitor' : ''}`}>
      {visitor && entry ? (
        <header className="today-top">
          <Link to="/" aria-label="Letters on Fire home"><Logo height={24} /></Link>
          <Link className="btn gold sm" to="/play">Play</Link>
        </header>
      ) : own ? (
        <div className="topbar">
          <button type="button" className="ibtn" aria-label="Back" onClick={() => (entry ? navigate('/') : navigate(-1))}><Icon name="back" /></button>
          <div className="title"><b>Your profile</b><small>ANYONE WITH THE LINK CAN SEE IT</small></div>
          <button type="button" className="ibtn" aria-label="Share your profile" onClick={() => void share()}><Icon name="share" /></button>
        </div>
      ) : (
        // Someone else's: the way back and a share; their name and badge join them once the header
        // has scrolled under the bar, which stays put.
        <div ref={bar} className={`topbar pf-bar${named ? ' scrolled' : ''}`}>
          <button type="button" className="ibtn" aria-label="Back" onClick={() => (entry ? navigate('/') : navigate(-1))}><Icon name="back" /></button>
          <div className={`named pf-bar-name${named ? ' on' : ''}`} aria-hidden={!named}><b className="ellipsis">{p.displayName}</b><NameBadge badge={p.badge} /></div>
          <button type="button" className="ibtn" aria-label={`Share ${p.displayName}’s profile`} onClick={() => void share()}><Icon name="share" /></button>
        </div>
      )}

      <header className="pf-head">
        <Avatar id={p.id} name={p.displayName} src={p.avatar} size={76} />
        <div className="who">
          <h1 className="named" ref={head}><span className="ellipsis">{p.displayName}</span><NameBadge badge={p.badge} size={26} /></h1>
          <span className="since">Since {sinceText(p.since)} · {fmt(p.editions)} edition{p.editions === 1 ? '' : 's'}</span>
          <div className="chips">
            {p.streak > 0 && <span className="chip"><Icon name="streak" />{p.streak}-day streak</span>}
            {p.usualTopPercent !== null && <span className="chip">Usually top {p.usualTopPercent}%</span>}
          </div>
        </div>
      </header>

      {own ? (
        <div className="pf-acts">
          <button type="button" className="btn gold" onClick={() => void share()}><Icon name="share" />Share profile</button>
          <button type="button" className="btn ghost" onClick={() => { setWanted(true); setPicking(true) }}>Choose badge</button>
        </div>
      ) : !visitor && p.record && (
        // In-app challenges go to players you've had one with; anyone else gets your link some other way.
        <div className="pf-acts">
          <ChallengeButton p={p} />
          <Link className="vs" to={`/rivals/${p.id}`}><b>{recordText(p.record, p.displayName).score}</b>You vs {p.displayName} ›</Link>
        </div>
      )}

      <div className="sec-head pf-sec"><h3>Badges</h3><span className="eyebrow">{eight.filter((c) => c.tier).length} of {eight.length} earned</span></div>
      {phoenix && <PhoenixCrown category={phoenix} shown={p.badge?.category === 'phoenix'} onOpen={() => setOpen('phoenix')} />}
      <BadgeGrid categories={p.categories} shown={p.badge?.category ?? null} onOpen={setOpen} />

      <div className="sec-head pf-sec"><h3>Stats</h3><span className="eyebrow">games on their day</span></div>
      <div className="pf-stats">{stats.map(([v, l]) => <div className="pf-st" key={l}><b>{v}</b><span>{l}</span></div>)}</div>

      {p.form.average !== null && <div className="pf-form"><Form form={p.form} title="Form" own={own} /></div>}

      {p.recent.length > 0 && (
        <>
          <div className="sec-head pf-sec"><h3>Recent achievements</h3><span className="eyebrow">newest first</span></div>
          <div className="card pf-recent">
            {p.recent.map((r, i) => (
              <div className="pf-rc" key={`${r.key}-${i}`}>
                <Badge category={r.category} tier={tierOf(r.category)} lit={r.category === 'phoenix' && phoenix ? phoenix.earned / phoenix.total : undefined} size={30} />
                <div><b>{r.name ?? 'A secret achievement'}</b><span>{r.name ? SHORT_NAME[r.category] : 'Find it yourself to see it'}</span></div>
                <span className="dt">{earnedOn(r.earnedAt)}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {own && (
        <div className="pf-link">
          <span className="ellipsis">{window.location.host}/p/<b>{p.handle}</b></span>
          <button type="button" className="btn plain" onClick={() => void copy()}>Copy</button>
        </div>
      )}

      {visitor && (
        <div className="pf-stick">
          <Link className="btn gold block" to="/play"><Icon name="flame" />Play today’s edition</Link>
          <span>{today ? `No. ${today.number} · ` : ''}free, no sign-up · think you can beat {p.displayName}?</span>
        </div>
      )}

      <CategorySheet category={p.categories.find((c) => c.id === open) ?? null} all={p.categories} owner={own ? null : p.displayName} onClose={() => setOpen(null)} />
      {own && mine && <BadgePicker open={picking} onClose={() => setPicking(false)} data={mine} />}
    </div>
  )
}

/** Sends them your challenge on today's edition, in the app: they see it on Today and in Rivals. */
function ChallengeButton({ p }: { p: PlayerProfile }) {
  const toast = useToast()
  const today = useEdition('today').data
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  async function send() {
    if (!today) return
    setBusy(true)
    try {
      const own = await api.post<Challenge>('/challenges', { edition: today.number })
      const updated = await api.post<Challenge>(`/challenges/${own.code}/invite`, { playerId: p.id })
      setResource(`challenge:${updated.code}`, updated)
      invalidate('mychallenges', `edchallenges:${today.number}`)
      setSent(true)
      toast(<><Icon name="check" />Sent to {p.displayName}</>)
    } catch (e) {
      toast(e instanceof ApiFailure && e.body.message ? e.body.message : 'Couldn’t send that. Try again.')
    } finally {
      setBusy(false)
    }
  }
  return (
    <button type="button" className="btn gold" disabled={busy || sent || !today} onClick={() => void send()}>
      <Icon name={sent ? 'check' : 'swords'} />{sent ? 'Challenge sent' : `Challenge ${p.displayName}`}
    </button>
  )
}
